/**
 * src/lib/docs/agents.ts
 *
 * Builds the `AGENTS.md` file content for the scaffolded project.
 * This document helps AI coding assistants understand the project
 * architecture, conventions, and CLI commands.
 */

import type { DbChoice, ValidatorChoice, TokenDelivery, PackageManager } from '../types';

export interface AgentDocsOptions {
  projectName: string;
  db: DbChoice;
  validator: ValidatorChoice;
  useAuth: boolean;
  useDocker: boolean;
  tokenDelivery?: TokenDelivery;
  pm?: PackageManager;
}

/** Maps a DB choice to a human-readable label. */
function dbLabel(db: DbChoice): string {
  return (
    {
      mongoose: 'Mongoose (MongoDB)',
      prisma: 'Prisma (PostgreSQL)',
      drizzle: 'Drizzle (PostgreSQL)',
    }[db] || db
  );
}

/** Returns the model/schema pattern description for the chosen DB. */
function modelPattern(db: DbChoice): string {
  switch (db) {
    case 'mongoose':
      return 'Mongoose schemas — `new Schema<IModel>({...})` exported as `model<IModel>(\'Name\', schema)`';
    case 'prisma':
      return 'Prisma schema — models defined in `prisma/schema.prisma`, accessed via singleton `prisma` client in `src/app/utils/prisma.ts`';
    case 'drizzle':
      return 'Drizzle schemas — tables defined with `pgTable()` in model files, accessed via `drizzle(pool)` in `src/app/utils/drizzle.ts`';
    default:
      return '';
  }
}

/**
 * Returns the DB migration command for the chosen DB and package manager,
 * or `null` if the DB manages migrations automatically (e.g. Mongoose).
 */
function migrationCmd(db: DbChoice, pm: PackageManager): string | null {
  switch (db) {
    case 'mongoose':
      return null;
    case 'prisma':
      return `${pm === 'npm' ? 'npx' : pm === 'yarn' ? 'yarn' : 'pnpm exec'} prisma migrate dev --name <migration_name>`;
    case 'drizzle':
      return `${pm === 'npm' ? 'npx' : pm === 'yarn' ? 'yarn' : 'pnpm exec'} drizzle-kit migrate`;
    default:
      return null;
  }
}

/**
 * Builds the full `AGENTS.md` content as a string.
 *
 * @param opts - Project configuration options chosen during scaffolding.
 * @returns The complete markdown document.
 */
export function buildAgentsMd(opts: AgentDocsOptions): string {
  const {
    projectName,
    db,
    validator,
    useAuth,
    useDocker,
    tokenDelivery,
    pm = 'npm',
  } = opts;

  const lines: string[] = [];

  lines.push(`# AGENTS.md`);
  lines.push('');
  lines.push(`> Auto-generated by [create-express-modular](https://create-express-modular.lovable.app/). This file helps AI coding assistants understand the project.`);
  lines.push('');

  lines.push(`## Project: ${projectName}`);
  lines.push('');
  lines.push(`A production-ready, domain-driven **Express + TypeScript** backend.`);
  lines.push('');

  lines.push('## Tech Stack');
  lines.push('');
  lines.push(`- **Runtime**: Node.js ≥ 18`);
  lines.push(`- **Framework**: Express`);
  lines.push(`- **Language**: TypeScript (strict)`);
  lines.push(`- **Database / ORM**: ${dbLabel(db)}`);
  lines.push(`- **Validation**: ${validator === 'zod' ? 'Zod' : 'Joi'}`);
  lines.push(`- **Package Manager**: ${pm}`);
  if (useAuth) {
    const delivery = tokenDelivery === 'cookie' ? 'HTTP-only cookies' : 'Authorization header';
    lines.push(`- **Auth**: JWT with bcrypt + rate limiting (${delivery})`);
  }
  if (useDocker) {
    lines.push(`- **Docker**: Dockerfile + docker-compose`);
  }
  lines.push('');

  lines.push('## Architecture Rules');
  lines.push('');
  lines.push('1. All source code lives under `src/`.');
  lines.push('2. **Modules** follow domain-driven design: `src/app/modules/<Name>/` with files named `<name>.controller.ts`, `<name>.service.ts`, `<name>.route.ts`, `<name>.model.ts`, `<name>.interface.ts`, `<name>.validation.ts`.');
  lines.push('3. **Middleware** files MUST be named `<name>.middleware.ts` inside `src/app/middlewares/`. The build will fail otherwise.');
  lines.push('4. Routes are auto-registered in `src/app/routes/index.ts` via inject markers (`// --- INJECT IMPORTS HERE ---` and `// --- INJECT ROUTES HERE ---`). Do NOT remove these markers.');
  lines.push('5. **All environment variables** go through `src/app/config/index.ts` — never use `process.env` directly in application code.');
  lines.push('6. Wrap all async route handlers with `catchAsync()` from `src/app/utils/catchAsync.ts`.');
  lines.push('7. Use `sendResponse()` from `src/app/utils/sendResponse.ts` for all API responses.');
  lines.push('8. Use `validateRequest()` middleware to validate incoming request bodies against schemas.');

  if (db === 'mongoose') {
    lines.push('9. Use `QueryBuilder` from `src/app/utils/QueryBuilder.ts` for search, filter, sort, and pagination on Mongoose queries.');
  }

  lines.push('');

  lines.push('## Model Pattern');
  lines.push('');
  lines.push(modelPattern(db));
  lines.push('');

  lines.push('## CLI Commands & Shortcuts');
  lines.push('');
  lines.push('Use the CEM CLI for scaffolding — do NOT create module/middleware files manually.');
  lines.push('');
  lines.push('| Command | Alias / Shortcut | Description |');
  lines.push('|---|---|---|');
  lines.push('| `cem add module <Name...>` | — | Scaffold module(s) with all files + auto-wire routes |');
  lines.push('| `cem add middleware <name...>` | — | Create middleware file(s) with correct naming |');
  lines.push('| `cem add env <KEY...>` | — | Add env var(s) to `.env`, `.env.example`, and `config/index.ts` |');
  lines.push('| `cem remove module <Name...>` | `cem rm module <Name...>` | Delete module(s) + unwire route(s) |');
  lines.push('| `cem remove middleware <name...>` | `cem rm middleware <name...>` | Delete middleware file(s) |');
  lines.push('| `cem remove env <KEY...>` | `cem rm env <KEY...>` | Remove env var(s) from all config files |');
  lines.push('| `cem list` | `cem ls` | Display project modules, routes, middlewares, env vars, and `cem-cli.json` settings |');
  lines.push('| `cem dev` | — | Start dev server with hot reload |');
  lines.push('| `cem build` | — | Convention guards + TypeScript compilation |');
  lines.push('| `cem check` | — | Type-check + lint + format check |');
  lines.push('');
  lines.push('> 💡 Project configuration is managed via `cem-cli.json` at the root.');
  lines.push('');

  const migrate = migrationCmd(db, pm);
  if (migrate) {
    lines.push('## Database Migrations');
    lines.push('');
    lines.push('```bash');
    lines.push(migrate);
    lines.push('```');
    lines.push('');
  }

  lines.push('## Do NOT');
  lines.push('');
  lines.push('- Create module files manually — use `cem add module`.');
  lines.push('- Use `process.env` directly — import `config` from `src/app/config`.');
  lines.push('- Rename middleware files without the `.middleware.ts` suffix — `cem build` will reject it.');
  lines.push('- Remove the inject markers in `src/app/routes/index.ts`.');
  lines.push('- Modify core middlewares (`globalErrorHandler`, `notFound`' + (useAuth ? ', `auth`, `rateLimiter`' : '') + ') unless intentional.');
  lines.push('');

  return lines.join('\n');
}

export { migrationCmd, dbLabel };
